import { Injectable } from "@angular/core";
import { ConfigService } from "../config/config.service";
import { CustomerModel } from "src/app/models/customer-model";
import { UserSessionService } from "../user-session/user-session.service";
import { WebservicesService } from "../webservices/webservices.service";
import { Subject, map } from "rxjs";
import type { PlatformResponse } from "src/app/interfaces/webservices-interface";

type TApiInputObjct = {
  customer_id?: number | null
  session_token?: string
  clientapp?: string
  return_objects?: number;
};

@Injectable({
  providedIn: "root",
})

export class LobbyApiService {
  private customer: CustomerModel;
  private vendorsThatSupportDemoPlay = {
    'ags': false,
    'everi': false,
    'evolution': false,
    'netent': false,
    'igt': true,
    'internal': true,
    'microgaming': true,
    'nyx': true,
    'slingo': true,
  };

  constructor(
    private userSession: UserSessionService,
    private webService: WebservicesService,
    private config: ConfigService,
  ) {
    this.userSession.getCustomer().subscribe(
      (customer) => {
        this.customer = customer;
      }
    );
    this.initializeDemoPlayConfig();
  }

  private initializeDemoPlayConfig() {
    if (this.config.getConfig()['FEATURE_VENDOR_DEMO']) {
      try {
        const configVendors = JSON.parse(this.config.getConfig()['FEATURE_VENDOR_DEMO'].replace(/'/g, '"'));
        this.vendorsThatSupportDemoPlay = configVendors;
      }
      catch (error) {
        console.error('Error parsing FEATURE_VENDOR_DEMO config:', error);
      }
    }
  }

  public setDemoPlayFlag(games: any) {
    if (Array.isArray(games)) {
      games.forEach(game => {
        const vendorIdent = game.vendor_ident?.toLowerCase();
        game.is_demoPlay = vendorIdent in this.vendorsThatSupportDemoPlay ? 
          this.vendorsThatSupportDemoPlay[vendorIdent] : false;
      });
    } else if (typeof games === 'object') {
      Object.values(games).forEach(categoryGames => {
        if (Array.isArray(categoryGames)) {
          categoryGames.forEach(game => {
            const vendorIdent = game.vendor_ident?.toLowerCase();
            game.is_demoPlay = vendorIdent in this.vendorsThatSupportDemoPlay ? 
              this.vendorsThatSupportDemoPlay[vendorIdent] : false;
          });
        }
      });
    }
    return games;
  }

  fetchGamesByLobbyName(lobbyName: string): Subject<PlatformResponse<any>> {
    const obj: TApiInputObjct = {
      clientapp: this.config.getClientApp(),
    }
    if (this.customer?.customerId) {
      obj.customer_id = this.customer?.customerId;
    }
    const session_token = this.userSession.getSessionToken();
    if (session_token) {
      obj.session_token = session_token;
    }

    return this.webService.get(`lobby/${lobbyName}/games`, obj).pipe(
      map(response => {
        if (response.payload) {
          response.payload = this.setDemoPlayFlag(response.payload);
   }
        return response;
      })
    ) as Subject<PlatformResponse<any>>;
  }

  fetchKeywords(lobbyName: string): Subject<PlatformResponse<any>> {
    const obj: TApiInputObjct = {
      clientapp: this.config.getClientApp(),
    }
    if (this.customer?.customerId) {
      obj.customer_id = this.customer?.customerId;
    }
    const session_token = this.userSession.getSessionToken();
    if (session_token) {
      obj.session_token = session_token;
    }
    return this.webService.get(`lobby/${lobbyName}/keywords`, obj);
  }

  fetchLobbiesKeywords(): Subject<PlatformResponse<any>> {
    const obj: TApiInputObjct = {
      clientapp: this.config.getClientApp(),
    }
    if (this.customer?.customerId) {
      obj.customer_id = this.customer?.customerId;
    }
    const session_token = this.userSession.getSessionToken();
    if (session_token) {
      obj.session_token = session_token;
    }
    return this.webService.get('lobby/get-lobbies-keywords', obj);
  }

  fetchLobbiesBanners(lobbyName: string, loggedIn: boolean): Subject<PlatformResponse<any>> {
    const obj: TApiInputObjct = {
      clientapp: this.config.getClientApp(),
    }
    if (loggedIn) {
      obj.customer_id = this.customer.customerId,
        obj.session_token = this.userSession.getSessionToken()
    }
    return this.webService.get(`lobby/${lobbyName}/banners`, obj);
  }

  getFavourites(): Subject<PlatformResponse<any>> {
    if (!this.customer) {
      return new Subject();
    }

    const obj = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.getSessionToken(),
    }

    return this.webService.get("lobby/get-favourite-games", obj).pipe(
      map(response => {
        if (response.payload) {
          response.payload = this.setDemoPlayFlag(response.payload);
        }
        return response;
      })
    ) as Subject<PlatformResponse<any>>;
  }

  getRecent(lobbyName: string, onlyLastPlayed = false): Subject<PlatformResponse<any>> {
    if (!this.customer) {
      return new Subject();
    }
    const obj: TApiInputObjct = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.getSessionToken(),
      clientapp: this.config.getClientApp(),
    }
    if (onlyLastPlayed) {
      obj.return_objects = 1
    }

    return this.webService.get(`lobby/${lobbyName}/recent`, obj).pipe(
      map(response => {
        if (response.payload) {
          response.payload = this.setDemoPlayFlag(response.payload);
        }
        return response;
      })
    ) as Subject<PlatformResponse<any>>;
  }

  addFavourites(game_id): Subject<PlatformResponse<any>> {
    if (!this.customer) {
      return new Subject();
    }
    const obj = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.getSessionToken(),
      launch_id: game_id,
    }
    return this.webService.post("/lobby/add-favourite-game", obj);
  }

  removeFavourites(game_id): Subject<PlatformResponse<any>> {
    if (!this.customer) {
      return new Subject();
    }
    const obj = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.getSessionToken(),
      launch_id: game_id,
    }
    return this.webService.post("lobby/remove-favourite-game", obj);
  }

  removeRecent(game_id) {
    if (!this.customer) {
      return new Subject();
    }
    const obj = {
      customer_id: this.customer.customerId,
      session_token: this.userSession.getSessionToken(),
      launch_id: game_id,
    }
    return this.webService.post("lobby/remove-recent-game", obj);
  }
}
